// CreateFamilyPage.js
// Lets user create a new family and get a join code to share

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Function to generate a random join code
// Creates something like "KANA-A1B2"
function generateJoinCode() {
  const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars like 0,O,1,I
  let code = 'KANA-';
  for (let i = 0; i < 4; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    code += characters[randomIndex];
  }
  return code;
}

function CreateFamilyPage({ user, userProfile, onFamilyCreated }) {
  // Form state
  const [familyName, setFamilyName] = useState('');

  // After creation, store the join code to display it
  const [joinCode, setJoinCode] = useState('');

  // Track if family has been created
  const [created, setCreated] = useState(false);

  // Error and loading states
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    if (familyName.trim().length < 2) {
      setError('Please enter a family name (at least 2 characters).');
      setLoading(false);
      return;
    }

    try {
      // Generate a unique join code
      const newJoinCode = generateJoinCode();

      // Create a new family document
      // Using user's ID as part of family ID makes it unique
      const familyId = `family_${user.uid}_${Date.now()}`;

      // Get user's first name from their profile
      // Use firstName or email as fallback
      const userDisplayName = userProfile?.firstName || user.email.split('@')[0];

      await setDoc(doc(db, 'families', familyId), {
        name: familyName.trim(),
        joinCode: newJoinCode,
        members: [user.uid],
        memberNames: {
          [user.uid]: userDisplayName
        },
        createdBy: user.uid,
        createdAt: serverTimestamp()
      });

      // Update user's profile with the family ID
      await updateDoc(doc(db, 'users', user.uid), {
        familyId: familyId
      });

      // Store the join code to display
      setJoinCode(newJoinCode);
      setCreated(true);

      // Notify App.js to refresh user profile
      if (onFamilyCreated) {
        onFamilyCreated();
      }
    } catch (err) {
      console.error('Error creating family:', err);
      setError('Failed to create family. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Function to copy join code to clipboard
  const copyCode = () => {
    navigator.clipboard.writeText(joinCode);
    alert('Code copied to clipboard!');
  };

  // If family is created, show the join code
  if (created) {
    return (
      <div className="family-page">
        <button
          onClick={() => navigate('/family-setup')}
          className="button button-secondary"
          style={{ marginBottom: 'var(--spacing-lg)', width: 'auto', maxWidth: '200px' }}
        >
          ← Back
        </button>

        <h1>Family created!</h1>
        <p>Share this code with your partner to join:</p>

        <div className="join-code-display">
          <div className="code">{joinCode}</div>
          <p>Tap to copy</p>
          <button
            onClick={copyCode}
            className="button button-secondary button-small"
            style={{ marginTop: '12px' }}
          >
            Copy code
          </button>
        </div>

        <button
          onClick={() => navigate('/todos')}
          className="button button-primary"
        >
          Go to my todos
        </button>
      </div>
    );
  }

  // Show the create family form
  return (
    <div className="family-page">
      <button
        onClick={() => navigate('/family-setup')}
        className="button button-secondary"
        style={{ marginBottom: 'var(--spacing-lg)', width: 'auto', maxWidth: '200px' }}
      >
        ← Back
      </button>

      <h1>Create your family</h1>
      <p>Give your family a name. You'll get a code to share with your partner.</p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="familyName">Family name</label>
          <input
            type="text"
            id="familyName"
            value={familyName}
            onChange={(e) => setFamilyName(e.target.value)}
            placeholder="e.g., The Smiths, Our Family"
            required
          />
        </div>

        {error && <p className="form-error">{error}</p>}

        <button
          type="submit"
          className="button button-primary"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create family'}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: 'var(--spacing-lg)' }}>
        <a
          href="/join-family"
          onClick={(e) => {
            e.preventDefault();
            navigate('/join-family');
          }}
          style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 600 }}
        >
          Need to join a family instead?
        </a>
      </p>
    </div>
  );
}

export default CreateFamilyPage;
